/**
 * WorkspaceMultiSelect - Componente para seleção múltipla de workspaces
 * Permite vincular categorias, produtos e tabelas de preço a múltiplos workspaces
 * 
 * Versão: 1.0.0
 * - Suporte a multi-seleção com checkboxes
 * - Exibição de badges coloridas
 * - Validação de mínimo 1 selecionado
 */
import * as React from 'react';
import { Check, ChevronDown, Layers, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Tipo para workspace
interface Workspace {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
}

// Props do componente
interface WorkspaceMultiSelectProps {
  /** IDs dos workspaces selecionados */
  value: string[];
  /** Callback ao alterar seleção */
  onChange: (ids: string[]) => void;
  /** Placeholder quando vazio */
  placeholder?: string;
  /** Se o campo é obrigatório (mínimo 1) */
  required?: boolean;
  /** Se o componente está desabilitado */
  disabled?: boolean;
  /** Classes adicionais */
  className?: string;
}

/**
 * Componente de multi-seleção de workspaces
 */
export function WorkspaceMultiSelect({
  value,
  onChange,
  placeholder = 'Selecione workspaces...',
  required = false,
  disabled = false,
  className,
}: WorkspaceMultiSelectProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = React.useState(false);

  // Buscar workspaces ativos do tenant
  const { data: workspaces = [], isLoading } = useQuery({
    queryKey: ['crm-workspaces-active', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name, icon, color')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Workspace[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // Cache de 5 minutos
  });

  // Toggle seleção de um workspace
  const handleToggle = (workspaceId: string) => {
    const isSelected = value.includes(workspaceId);
    
    if (isSelected) {
      // Não permitir remover se é required e só tem 1 selecionado
      if (required && value.length === 1) {
        return;
      }
      onChange(value.filter((id) => id !== workspaceId));
    } else {
      onChange([...value, workspaceId]);
    }
  };

  // Remover workspace selecionado
  const handleRemove = (workspaceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Não permitir remover se é required e só tem 1 selecionado
    if (required && value.length === 1) {
      return;
    }
    
    onChange(value.filter((id) => id !== workspaceId));
  };

  // Obter workspaces selecionados
  const selectedWorkspaces = workspaces.filter((ws) => value.includes(ws.id));

  // Verificar se há erro de validação
  const hasError = required && value.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            'w-full justify-between h-auto min-h-10 px-3 py-2',
            hasError && 'border-destructive',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedWorkspaces.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedWorkspaces.map((ws) => (
                <Badge
                  key={ws.id}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                  style={{
                    backgroundColor: ws.color ? `${ws.color}20` : undefined,
                    borderColor: ws.color || undefined,
                  }}
                >
                  <Layers className="h-3 w-3" style={{ color: ws.color || undefined }} />
                  <span className="max-w-[100px] truncate">{ws.name}</span>
                  {(!required || value.length > 1) && (
                    <button
                      type="button"
                      onClick={(e) => handleRemove(ws.id, e)}
                      className="ml-1 rounded-full hover:bg-background/50 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar workspace..." />
          <CommandList>
            <CommandEmpty>Nenhum workspace encontrado.</CommandEmpty>
            <CommandGroup>
              {workspaces.map((ws) => {
                const isSelected = value.includes(ws.id);
                
                return (
                  <CommandItem
                    key={ws.id}
                    value={ws.name}
                    onSelect={() => handleToggle(ws.id)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    <div
                      className="h-5 w-5 rounded flex items-center justify-center mr-2"
                      style={{ backgroundColor: ws.color ? `${ws.color}20` : undefined }}
                    >
                      <Layers className="h-3 w-3" style={{ color: ws.color || undefined }} />
                    </div>
                    <span>{ws.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
